import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import { Trans, withI18n, withI18nProps } from '@lingui/react'
import isEmpty from 'lodash/isEmpty'
import compose from 'lodash/fp/compose'
import cn from 'classnames'

import uiBox, { DecoratorProps } from 'client/shared/decorators/ui-box'
import { withRouter } from 'react-router'
import { Location, Router } from 'client/shared/types/react-router'

import urlFor, {
  addParamsToPath,
  NullableQueryParams,
} from 'shared/tools/url-helper'
import {
  aboutTabHelper,
  impressionsTabHelper,
  checkIfTabExists,
  generateTabs,
} from 'client/shared/helpers/tabs-helper'

import Spacer from 'client/shared/blocks/spacer'
import LayoutInfo from 'client/bookmate/blocks/layout-info'
import HorizontalScroller from 'client/shared/blocks/horizontal-scroller'
import ResourceTabs from 'client/bookmate/blocks/resource-tabs'

import { ResourceLead } from 'client/bookmate/blocks/resource-lead/resource-lead'
import BadgeList from 'client/shared/blocks/badge-list'
import EmotionRating from 'client/bookmate/blocks/emotion-rating'
import BookContextMenuBox from 'client/shared/boxes/book-context-menu-box'
import BookReadersIndexBox from 'client/bookmate/boxes/book-readers-index-box'
import WishlistButtonBox from 'client/shared/boxes/wishlist-button-box'

import AddButton from 'client/shared/blocks/add-button'
import { ShareButton } from 'client/shared/blocks/share-button'
import ButtonsRow from 'client/shared/blocks/buttons-row'

import Button from 'client/shared/blocks/button'
import { Heading } from 'client/shared/blocks/heading'
import { Loader } from 'client/shared/blocks/loader'
import Linka from 'client/shared/blocks/linka'

import { AuthorsList } from 'client/shared/blocks/authors-list'
import { SerialProps } from 'client/shared/types/serial'
import { EmotionRatingProps } from 'client/shared/types/emotion'
import { State as AppState } from 'client/shared/reducers/app-reducer'

import './index.styl'
import { State } from 'shared/types/redux'
import {
  analyticsEvent,
  CONTENT_VIEWED,
} from 'client/shared/reducers/analytics-reducer'
import { getCurrentDateTime } from 'shared/tools/date-helper'
import { ResourceProps } from 'client/shared/types/resource'
import { sendUnavailableItemEvent } from 'shared/tools/analytics-helper'
import { isShareAvailable } from 'client/shared/helpers/socials-helpers'

type Props = {
  tab: { tabType?: string; tabTitle: string; count: number }
  serial: SerialProps
  related: []
  readers: []
  emotionRating: EmotionRatingProps[]
  fromSeries?: string
  currentPage: string
  app: AppState
  location: Location
  userId: number
  country: string
  publisherResources: ResourceProps[]
  router: Router
} & DecoratorProps &
  withI18nProps

class SerialInfo extends Component<Props> {
  componentDidMount(): void {
    const {
      dispatch,
      userId,
      serial: { resourceType, uuid, can_be_read },
    } = this.props
    const dateTime = getCurrentDateTime()

    dispatch(
      analyticsEvent(CONTENT_VIEWED, {
        event_datetime: dateTime,
        user_id: userId,
        content_type: resourceType,
        content_uuid: uuid,
      }),
    )

    if (!can_be_read) {
      sendUnavailableItemEvent()
    }
  }

  getShareData() {
    const {
      i18n,
      locale,
      serial: { uuid, title },
      app: { domain },
      router,
    } = this.props

    const url = urlFor.serialWithDomain(
      uuid,
      locale,
      domain,
      router?.location?.query,
    )

    return {
      facebook: { url },
      twitter: {
        url,
        title: i18n._('social.twitter_serial_share', { title }),
      },
    }
  }

  render() {
    const { serial } = this.props

    if (isEmpty(serial)) {
      return (
        <>
          <Spacer size="top-page" />
          <Loader color="gray" kind="big" centered />
          <Spacer size="bottom-page" />
        </>
      )
    }
    const { isOneColumnSize, isMobileOS, app } = this.props

    return (
      <LayoutInfo
        lead={
          <ResourceLead
            query={app.storedQuery}
            isOneColumnSize={isOneColumnSize()}
            isMobileOS={isMobileOS()}
            resource={serial}
            serialInfo={urlFor.getSerialReaderQuery(serial)}
          />
        }
        header={this.renderHeader}
        body={this.renderBody}
        aside={this.renderAside}
        withFloatingCols
      />
    )
  }

  renderHeader = () => {
    const {
      isOneColumnSize,
      isMobileSize,
      isTabletSize,
      serial: { title, authors },
    } = this.props

    const isDevice = isTabletSize() || isMobileSize()

    return (
      <div>
        <AuthorsList underlined authors={authors} max={5} />
        <Spacer size={8} />
        <Heading kind="kazimir" looksLike={isOneColumnSize() ? 3 : 1}>
          {title}
        </Heading>
        <Spacer />
        {isDevice && (
          <>
            {this.renderEmotionRating()}
            <Spacer />
          </>
        )}
        {!isMobileSize() && this.renderCTA()}
      </div>
    )
  }

  renderBody = () => {
    const { isMobileSize } = this.props

    return isMobileSize() ? <div>{this.renderCTA()}</div> : null
  }

  renderAside = () => {
    return <div>{this.renderEmotionRating()}</div>
  }

  renderReadButtons() {
    const {
      serial,
      serial: { can_be_read, restricted_reading_on_web, uuid },
      isMobileOS,
      isMobileSize,
      isTabletSize,
      userId,
      router,
      country,
    } = this.props

    const showMobileButton =
      isMobileOS() &&
      isMobileSize() &&
      can_be_read &&
      !restricted_reading_on_web

    const serialInfo = urlFor.getSerialReaderQuery(serial)
    const readerPath = urlFor.reader(
      serial.uuid,
      {
        resourceType: 'serial',
      },
      { ...router?.location?.query, serialInfo },
    )

    return (
      <div className="buttons-row__wrapper">
        {!can_be_read && (
          <ButtonsRow>
            <WishlistButtonBox book={serial} />
            <BookContextMenuBox
              resource={serial}
              resourceType="serial"
              kind="square"
            />
            {isShareAvailable(country) && (
              <ShareButton
                kind="medium"
                shareData={this.getShareData()}
                analytics={{
                  object_type: 'book',
                  object_id: uuid,
                }}
              />
            )}
          </ButtonsRow>
        )}
        {can_be_read && (
          <ButtonsRow>
            <AddButton
              userId={userId}
              resource={serial}
              serialInfo={serialInfo}
            />
            <BookContextMenuBox
              resource={serial}
              resourceType="serial"
              kind="square"
            />
            {isShareAvailable(country) && (
              <ShareButton
                kind="medium"
                shareData={this.getShareData()}
                analytics={{
                  object_type: 'book',
                  object_id: uuid,
                }}
              />
            )}
            {isMobileOS() && isTabletSize() && !restricted_reading_on_web && (
              <Button kind="transparent" type="inline" path={readerPath} target>
                <Trans id="buttons.read_in_browser" />
              </Button>
            )}
          </ButtonsRow>
        )}
        {showMobileButton && (
          <div>
            <Spacer size={8} />
            <Button kind="transparent" path={readerPath} target>
              <Trans id="buttons.read_in_browser" />
            </Button>
          </div>
        )}
      </div>
    )
  }

  renderCTA() {
    const {
      serial,
      isDesktopAllSizes,
      isOneColumnSize,
      currentPage,
      readers,
    } = this.props
    const { uuid, labels, language } = serial

    const tabs = this.getTabs()
    const isAboutTab = aboutTabHelper(currentPage, uuid)

    const MAX_TABS_FIT_PER_COLUMN = 7
    const HAS_MANY_TABS = tabs.length >= MAX_TABS_FIT_PER_COLUMN

    const hasReaders = readers.length > 0

    return (
      <div className="serial-info__tablet-wrapper">
        {this.renderReadButtons()}
        {hasReaders && (
          <div>
            <Spacer />
            <BookReadersIndexBox resourceType="serial" />
          </div>
        )}
        <Spacer size={isDesktopAllSizes() ? 40 : 32} />
        {isDesktopAllSizes() && !HAS_MANY_TABS ? (
          <div>
            <ResourceTabs tabs={tabs} isAboutTab={isAboutTab} />
            <Spacer size={8} />
          </div>
        ) : (
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          <HorizontalScroller>
            <ResourceTabs tabs={tabs} isAboutTab={isAboutTab} />
          </HorizontalScroller>
        )}
        {isOneColumnSize() && isAboutTab && (
          <div>
            <BadgeList labels={labels} language={language} />
            <Spacer size={8} />
          </div>
        )}
      </div>
    )
  }

  renderEmotionRating() {
    const {
      emotionRating,
      serial,
      isDesktopAllSizes,
      currentPage,
      app,
    } = this.props
    const { uuid } = serial
    const { currentLocationHref } = app

    if (!emotionRating.length) return null

    const isAboutTab = aboutTabHelper(currentPage, uuid)
    const isImpressionsTab = impressionsTabHelper(currentLocationHref)

    const impressionsTabPath = isAboutTab
      ? addParamsToPath(
          `/serials/${uuid}`,
          app.storedQuery as NullableQueryParams,
        )
      : addParamsToPath(
          `/serials/${uuid}/impressions`,
          app.storedQuery as NullableQueryParams,
        )

    return (
      <Fragment>
        <Linka
          path={impressionsTabPath}
          className={cn('emotion-rating__link', {
            'is-impressions-tab': isImpressionsTab,
          })}
          replace={false}
          loginWall={false}
        >
          {isDesktopAllSizes() ? (
            <EmotionRating
              resource={serial}
              kind="medium"
              emotionRating={emotionRating}
            />
          ) : (
            <EmotionRating kind="small" emotionRating={emotionRating} />
          )}
        </Linka>
      </Fragment>
    )
  }

  getTabs() {
    const {
      auth,
      i18n,
      related = [],
      location: { query },
      publisherResources,
      serial: {
        impressions_count,
        quotes_count,
        bookshelves_count,
        episodes_count,
        readers_count,
        resourceType,
        uuid,
      },
    } = this.props

    const AVAILABLE_TABS = [
      { tabTitle: i18n._('book.about'), count: 1 },
      {
        tabType: 'episodes',
        tabTitle: i18n._('book.serial_episodes_title'),
        count: episodes_count,
      },
      {
        tabType: 'impressions',
        tabTitle: i18n._('book.impressions_header'),
        count: impressions_count,
      },
      {
        tabType: 'quotes',
        tabTitle: i18n._('book.quotes_header'),
        count: quotes_count,
      },
      {
        tabType: 'readers',
        tabTitle: i18n._('book.readers'),
        count: readers_count,
        pseudo: !auth,
      },
      {
        tabType: 'shelves',
        tabTitle: i18n._('book.on_shelves'),
        count: bookshelves_count,
      },
      {
        tabType: 'related',
        tabTitle: i18n._('book.related'),
        count: related.length,
      },
      {
        tabType: 'publisher',
        tabTitle: i18n._('book.publisher_resources'),
        count: publisherResources.length,
      },
    ]

    const tabs = generateTabs({
      resourceType,
      uuid,
      tabs: AVAILABLE_TABS.filter(tab => checkIfTabExists(tab.count)),
      query,
    })

    return tabs
  }
}

const connectWrapper = connect((state: State) => ({
  app: state.app,
  auth: state.currentUser.auth,
  related: state.serial.related,
  readers: state.serial.readers,
  userId: state.currentUser.data.id,
  publisherResources: state.serial.publisherResources,
}))

const wrapper = compose(
  withI18n({ update: true }),
  connectWrapper,
  withRouter,
  uiBox,
)

export default wrapper(SerialInfo)
